/**
 * ContrastBadge (Story 14.5 — AC #14)
 *
 * Inline WCAG 2.1 contrast-ratio indicator shown next to color inputs in the
 * Branding settings page. Uses the shared `computeContrastRatio` helper so the
 * UI number matches the server's contrast-warning decision exactly.
 *
 * Tiers:
 *   ≥ 7:1 — AAA (green, CheckCircle)
 *   ≥ 4.5:1 — AA (green-light, CheckCircle)
 *   ≥ 3:1 — NEAR-FAIL (amber, WarningCircle)
 *   < 3:1 — FAIL (red, WarningCircle)
 *
 * A11y (WCAG 2.2): icon + text label — never color alone.
 */

import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { computeContrastRatio } from "@provenleap/types";

export interface ContrastBadgeProps {
  /** Foreground / brand hex color being evaluated. */
  colorHex: string | null;
  /**
   * Which default text backdrop to measure against.
   * - "white" → #ffffff page backdrop
   * - "deep"  → #111827 deep-gray default text
   */
  background: "white" | "deep";
  /** Optional sub-label shown under the pill (e.g., "Against white background"). */
  label?: string;
}

const WHITE_HEX = "#ffffff";
const DEEP_GRAY_HEX = "#111827";

function tierFromRatio(ratio: number): {
  label: string;
  detail: string;
  bgClass: string;
  textClass: string;
  Icon: typeof CheckCircle;
} {
  if (ratio >= 7) {
    return {
      label: "AAA",
      detail: "Great — readable for users with low vision too (WCAG AAA).",
      bgClass: "bg-emerald-100 text-emerald-900",
      textClass: "text-emerald-900",
      Icon: CheckCircle,
    };
  }
  if (ratio >= 4.5) {
    return {
      label: "AA",
      detail: "Readable for most users (WCAG AA).",
      bgClass: "bg-emerald-50 text-emerald-800",
      textClass: "text-emerald-800",
      Icon: CheckCircle,
    };
  }
  if (ratio >= 3) {
    // CR1 M8: NEAR-FAIL is a distinct tier with its own label + tooltip so
    // sighted and assistive-tech users can tell amber (borderline) apart from
    // red (clear failure). Both fail WCAG AA (4.5:1), but the remediation
    // guidance differs.
    return {
      label: "LOW",
      detail:
        "Readable for sighted users but below WCAG AA. Consider a slightly darker or lighter tone for accessibility.",
      bgClass: "bg-amber-100 text-amber-900",
      textClass: "text-amber-900",
      Icon: WarningCircle,
    };
  }
  return {
    label: "FAIL",
    detail:
      "Clients may have trouble reading text on this color. Consider making it darker or lighter.",
    bgClass: "bg-red-100 text-red-900",
    textClass: "text-red-900",
    Icon: WarningCircle,
  };
}

export function ContrastBadge({
  colorHex,
  background,
  label,
}: ContrastBadgeProps) {
  if (!colorHex) return null;

  const bgHex = background === "white" ? WHITE_HEX : DEEP_GRAY_HEX;
  // For WCAG, we compute the contrast of the brand color against the chosen
  // text/background reference. Callers pick which axis they care about.
  const ratio = computeContrastRatio(colorHex, bgHex);
  const tier = tierFromRatio(ratio);
  const { Icon } = tier;

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <span
        role="status"
        aria-label={`Contrast ratio ${ratio}:1 (${tier.label})`}
        title={tier.detail}
        className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[12px] font-medium ${tier.bgClass}`}
      >
        <Icon size={14} weight="fill" />
        <span>
          {ratio.toFixed(2)}:1 {tier.label}
        </span>
      </span>
      {label ? (
        <span className="text-[11px] text-muted-foreground">{label}</span>
      ) : null}
    </div>
  );
}
